import React, { createContext, useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { projectsApi } from '../api/projects';
import { storiesApi } from '../api/stories';
import {
  getPreferences,
  hasCompletedOnboarding as checkOnboardingComplete,
  savePreferences,
  completeOnboarding as markOnboardingComplete
} from '../utils/preferences';
import {
  forgetOpenProject,
  forgetOpenStory,
  isNotFound,
  readLastProjectId,
  readLastStoryId,
  rememberProjectId,
  rememberStoryId,
} from '../utils/lastProject';
import { PROJECT_OPEN_FAILED } from '../utils/writerCopy';
import { retryUnauthorized } from '../utils/retryUnauthorized';
import { retryTransient } from '../utils/retryTransient';

const AppContext = createContext();

export { AppContext };

// F9 plan Part B: refresh-safety for pages with no story-identifying URL
// segment (Book Notes, Bookmarks, Search, the docked reference panels).
// Namespaced to match the existing brainstormy.jetage.* persisted-UI-state
// convention (BrainstormyLayout.jsx's STORAGE_KEYS, RelationshipGraphPage.jsx)
// rather than AppContext's own older, unprefixed sidebar.* keys — this is
// app-level context, not panel-local UI state.
// /plottrack/:id, /scene-writing/:id, and /chat/:id carry their own
// story-identifying route param — their own resolution
// (useSyncStoryContext) is canonical there, so sessionStorage hydration
// must not race with it. Legacy /board/:id redirects into PlotTrack and
// is treated the same while the URL still matches. Every other route
// has no such param to derive from.
function routeHasStoryParam(pathname) {
  return /^\/plottrack\/[^/]+/.test(pathname)
    || /^\/board\/[^/]+/.test(pathname)
    || /^\/scene-writing\/[^/]+/.test(pathname)
    || /^\/chat\/[^/]+/.test(pathname);
}

export function AppProvider({ children }) {
  const [currentProject, setCurrentProject] = useState(null);
  const [currentStory, setCurrentStory] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [projectOpenError, setProjectOpenError] = useState(null);
  const [projectRestoreNonce, setProjectRestoreNonce] = useState(0);
  const [stories, setStories] = useState([]);

  // story-picker-stale-list-edge-cases (AC1): a lightweight revalidation
  // signal, bumped whenever a story is deleted anywhere in the app (see
  // NavigationTree's handleStoryDeleted). StoryBoardPage's own projectStories
  // fetch depends on this so a same-page sidebar delete refetches the
  // Story Board's story list without a project switch or reload — no new
  // polling, just one more dependency on the existing fetch effect.
  const [storyListVersion, setStoryListVersion] = useState(0);
  const bumpStoryListVersion = useCallback(() => setStoryListVersion((v) => v + 1), []);

  // Sidebar tab state (persisted to localStorage)
  const [activeTab, setActiveTabState] = useState(() => {
    return localStorage.getItem('sidebar.activeTab') || 'current';
  });
  const [activeSubTabs, setActiveSubTabs] = useState(() => {
    const stored = localStorage.getItem('sidebar.activeSubTabs');
    return stored ? JSON.parse(stored) : {};
  });

  // Persist tab state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar.activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('sidebar.activeSubTabs', JSON.stringify(activeSubTabs));
  }, [activeSubTabs]);

  // Tab state helpers
  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
  }, []);

  const getActiveSubTab = useCallback((projectId) => {
    return activeSubTabs[projectId] || 'foundation';
  }, [activeSubTabs]);

  const setActiveSubTab = useCallback((projectId, tab) => {
    setActiveSubTabs(prev => ({ ...prev, [projectId]: tab }));
  }, []);

  // Onboarding state (initialized synchronously from localStorage to prevent redirect race)
  const [onboardingComplete, setOnboardingComplete] = useState(() => checkOnboardingComplete());
  const [currentMedium, setCurrentMedium] = useState(null);
  const [workingStyle, setWorkingStyle] = useState(null);

  // Session Summary Service - session tracking
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const previousSessionIdRef = useRef(null);

  // Debug panel state (admin-only)
  const [lastDebugData, setLastDebugData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Hydrate preferences from localStorage on mount (onboardingComplete already initialized above)
  useEffect(() => {
    if (onboardingComplete) {
      const prefs = getPreferences();
      // Set most recent medium as current
      if (prefs.last_medium) {
        setCurrentMedium(prefs.last_medium);
        setWorkingStyle(prefs.last_working_style);
      } else if (prefs.mediums_used && prefs.mediums_used.length > 0) {
        const lastMedium = prefs.mediums_used[prefs.mediums_used.length - 1];
        setCurrentMedium(lastMedium);
        setWorkingStyle(prefs.working_styles?.[lastMedium]);
      }
    }
  }, [onboardingComplete]);

  /**
   * Complete the onboarding flow.
   * Updates both localStorage and context state.
   */
  const completeOnboardingFlow = useCallback((medium, style) => {
    savePreferences({ medium, workingStyle: style });
    markOnboardingComplete();
    setOnboardingComplete(true);
    setCurrentMedium(medium);
    setWorkingStyle(style);
  }, []);

  const projectsLoadGen = useRef(0);

  // Upload creates the book before Digest approved. Put it on the list
  // now. A list request already in flight is an older generation and
  // cannot replace this row when it lands.
  const noteCreatedProject = useCallback((project) => {
    if (!project?.id) return;
    const row = {
      id: project.id,
      name: project.name || 'Untitled',
      description: project.description ?? null,
      medium: project.medium || 'novel',
      is_implicit: project.is_implicit ?? !project.is_series,
      is_series: Boolean(project.is_series),
      accent_color: project.accent_color || null,
      created_at: project.created_at || new Date().toISOString(),
      last_activity: project.last_activity || new Date().toISOString(),
      story_count: project.story_count ?? (project.story_id ? 1 : 0),
      discovery_session_id: project.discovery_session_id || project.first_session_id || null,
      story_id: project.story_id || null,
    };
    setProjects((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.some((item) => item.id === row.id)) return list;
      return [row, ...list];
    });
  }, []);

  const loadProjects = useCallback(async () => {
    const gen = projectsLoadGen.current + 1;
    projectsLoadGen.current = gen;
    try {
      // A 401 here is usually a sign-in token that was not ready. Try
      // again before telling the writer the list failed.
      // A deploy restart (502/503/504 or network error) is retried too.
      const data = await retryTransient(
        () => retryUnauthorized(() => projectsApi.list()),
      );
      if (gen !== projectsLoadGen.current) return;
      setProjects(data);
      setProjectsError(null);
    } catch (error) {
      if (gen !== projectsLoadGen.current) return;
      // Fixed, human-readable message — the raw error (HTTP status text,
      // network error string, etc.) is not something a user should see
      // verbatim; console.error carries it for debugging instead.
      console.error('Failed to load projects:', error);
      setProjectsError('Something went wrong loading your projects. Please try again.');
    } finally {
      if (gen === projectsLoadGen.current) setProjectsLoaded(true);
    }
  }, []);

  const loadStories = useCallback(async (projectId) => {
    if (!projectId) {
      setStories([]);
      return;
    }
    try {
      const data = await storiesApi.list(projectId);
      setStories(data);
    } catch (error) {
      console.error('Failed to load stories:', error);
    }
  }, []);

  const createProject = useCallback(async (projectData) => {
    try {
      const newProject = await projectsApi.create(projectData);
      await loadProjects();
      return newProject;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  }, [loadProjects]);

  const createStory = useCallback(async (projectId, storyData) => {
    try {
      const newStory = await storiesApi.create(projectId, storyData);
      await loadStories(projectId);
      return newStory;
    } catch (error) {
      console.error('Failed to create story:', error);
      throw error;
    }
  }, [loadStories]);

  // Session Summary Service - session navigation handling
  const navigateToSession = useCallback((sessionId, sessionData = null) => {
    // Track the previous session for summary generation trigger
    if (currentSessionId && currentSessionId !== sessionId) {
      previousSessionIdRef.current = currentSessionId;
    }
    setCurrentSessionId(sessionId);
    setCurrentSession(sessionData);
    setSessionStatus(null); // Reset status, will be updated on next message
  }, [currentSessionId]);

  // Update current session data (e.g., after loading session details)
  const updateCurrentSession = useCallback((sessionData) => {
    setCurrentSession(sessionData);
  }, []);

  const getPreviousSessionId = useCallback(() => {
    return previousSessionIdRef.current;
  }, []);

  const clearPreviousSession = useCallback(() => {
    previousSessionIdRef.current = null;
  }, []);

  const updateSessionStatus = useCallback((status) => {
    setSessionStatus(status);
  }, []);

  // Clear current session (used when session/project/story is deleted)
  const clearCurrentSession = useCallback(() => {
    setCurrentSessionId(null);
    setCurrentSession(null);
    setSessionStatus(null);
    previousSessionIdRef.current = null;
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // F9 plan Part B: on cold load/refresh at a route with no story-identifying
  // param (Book Notes, Bookmarks, Search, docked panels), fall back to
  // whatever project/story was last active, so those pages don't go blank on
  // refresh. The URL wins whenever it has an answer — skipped on
  // plottrack/chat. Try again bumps projectRestoreNonce and runs this again.
  useEffect(() => {
    if (routeHasStoryParam(window.location.pathname)) return undefined;
    const lastProjectId = readLastProjectId();
    if (!lastProjectId) return undefined;
    const lastStoryId = readLastStoryId();
    let cancelled = false;
    (async () => {
      let project;
      try {
        project = await retryUnauthorized(() => projectsApi.get(lastProjectId));
      } catch (err) {
        if (cancelled) return;
        if (isNotFound(err)) {
          forgetOpenProject();
          setProjectOpenError(null);
          return;
        }
        // Timeout, 5xx, or a dropped connection: keep the id and let the
        // writer try again. The first paint must not forget the book.
        if (lastStoryId) rememberStoryId(lastStoryId);
        setProjectOpenError(PROJECT_OPEN_FAILED);
        return;
      }
      if (cancelled || !project) return;
      if (lastStoryId) {
        try {
          const story = await retryUnauthorized(() => storiesApi.get(lastStoryId));
          if (cancelled) return;
          if (story && story.project_id === project.id) {
            setProjectOpenError(null);
            setProjectAndStory(project, story);
            return;
          }
          forgetOpenStory();
        } catch (err) {
          if (cancelled) return;
          if (isNotFound(err)) {
            forgetOpenStory();
          } else {
            rememberStoryId(lastStoryId);
            setProjectOpenError(PROJECT_OPEN_FAILED);
            return;
          }
        }
      }
      if (!cancelled) {
        setProjectOpenError(null);
        setCurrentProject(project);
      }
    })();
    return () => { cancelled = true; };
    // setProjectAndStory is declared below and is stable. Try again is the
    // only reason to run this again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectRestoreNonce]);

  // Remember the open project only while one is open. A null on first
  // paint, or a failed load, must not delete the stored id. Deliberate
  // exits call leaveProject(). Switching project writes the new id here.
  useEffect(() => {
    if (currentProject?.id) rememberProjectId(currentProject.id);
  }, [currentProject]);

  // The story id starts null. That first paint must not wipe a stored
  // book before hydration reads it. Clearing happens once a story was
  // actually open and then left (delete, series altitude, another book).
  const storyWasOpen = useRef(false);
  useEffect(() => {
    if (currentStory?.id) {
      storyWasOpen.current = true;
      rememberStoryId(currentStory.id);
    } else if (storyWasOpen.current) {
      forgetOpenStory();
    }
  }, [currentStory]);

  const leaveProject = useCallback(() => {
    forgetOpenProject();
    setProjectOpenError(null);
    setCurrentProject(null);
    setCurrentStory(null);
  }, []);

  const retryProjectRestore = useCallback(() => {
    setProjectOpenError(null);
    setProjectRestoreNonce((n) => n + 1);
  }, []);

  // Track the story that should be preserved when project changes
  const preservedStoryRef = useRef(null);

  useEffect(() => {
    if (currentProject) {
      loadStories(currentProject.id);
      // Only reset currentStory if there's no preserved story or it doesn't match
      if (preservedStoryRef.current) {
        // Check if the preserved story belongs to this project
        if (preservedStoryRef.current.project_id === currentProject.id) {
          // Keep the preserved story, don't reset
          console.log('[AppContext] Preserving story:', preservedStoryRef.current.name);
        } else {
          // Story doesn't belong to this project, reset
          setCurrentStory(null);
        }
        // Clear the preserved story ref after handling
        preservedStoryRef.current = null;
      } else {
        // Check if currentStory already belongs to this project (don't reset if so)
        // This handles cases where project is re-set but story should persist
        setCurrentStory(prevStory => {
          if (prevStory && prevStory.project_id === currentProject.id) {
            console.log('[AppContext] Keeping existing story:', prevStory.name);
            return prevStory;
          }
          return null;
        });
      }
    } else {
      setStories([]);
      setCurrentStory(null);
    }
  }, [currentProject]);

  // A single-story project has no meaningful story choice — select the story
  // automatically so story-scoped surfaces (Book Notes, Story Board)
  // work without the hidden story-picker step. Series first-open (#682)
  // must stay at series altitude: do not greet with a book's Talk /
  // Snapshots / Outline.
  useEffect(() => {
    if (!currentProject || currentProject.is_series || stories.length !== 1) return;
    setCurrentStory(prevStory => {
      if (prevStory) return prevStory;
      const onlyStory = stories[0];
      if (onlyStory.project_id && onlyStory.project_id !== currentProject.id) {
        return prevStory;
      }
      return onlyStory;
    });
  }, [stories, currentProject]);

  /**
   * Set both project and story together (used when loading from session context).
   * This prevents the useEffect from resetting currentStory.
   */
  const setProjectAndStory = useCallback((project, story) => {
    // Store the story to preserve it through the project change
    preservedStoryRef.current = story;
    setCurrentProject(project);
    setCurrentStory(story);
  }, []);

  const refreshStories = useCallback(
    () => loadStories(currentProject?.id),
    [loadStories, currentProject?.id],
  );

  // A parent re-render (a /users/me refresh) must not hand every reader a
  // new context object. Effects that close streams and dialogs stay put.
  const value = useMemo(() => ({
    currentProject,
    setCurrentProject,
    currentStory,
    setCurrentStory,
    setProjectAndStory,
    projects,
    projectsLoaded,
    projectsError,
    projectOpenError,
    setProjectOpenError,
    projectRestoreNonce,
    retryProjectRestore,
    leaveProject,
    stories,
    storyListVersion,
    bumpStoryListVersion,
    refreshProjects: loadProjects,
    noteCreatedProject,
    refreshStories,
    createProject,
    createStory,
    activeTab,
    setActiveTab,
    getActiveSubTab,
    setActiveSubTab,
    onboardingComplete,
    currentMedium,
    workingStyle,
    completeOnboardingFlow,
    currentSessionId,
    currentSession,
    sessionStatus,
    navigateToSession,
    updateCurrentSession,
    getPreviousSessionId,
    clearPreviousSession,
    updateSessionStatus,
    clearCurrentSession,
    lastDebugData,
    setLastDebugData,
    isAdmin,
    setIsAdmin,
  }), [
    currentProject,
    currentStory,
    setProjectAndStory,
    projects,
    projectsLoaded,
    projectsError,
    projectOpenError,
    projectRestoreNonce,
    retryProjectRestore,
    leaveProject,
    stories,
    storyListVersion,
    bumpStoryListVersion,
    loadProjects,
    noteCreatedProject,
    refreshStories,
    createProject,
    createStory,
    activeTab,
    setActiveTab,
    getActiveSubTab,
    setActiveSubTab,
    onboardingComplete,
    currentMedium,
    workingStyle,
    completeOnboardingFlow,
    currentSessionId,
    currentSession,
    sessionStatus,
    navigateToSession,
    updateCurrentSession,
    getPreviousSessionId,
    clearPreviousSession,
    updateSessionStatus,
    clearCurrentSession,
    lastDebugData,
    isAdmin,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
